/**
 * Firebase initialization for Peskedo.
 *
 * Reads the standard `VITE_FIREBASE_*` variables from the environment (see
 * `.env.example`). The app is initialized once (guarded against HMR / multiple
 * imports) and exposes the shared `auth` instance used by `AuthContext`.
 *
 * Setup:
 *   1. Enable Email/Password sign-in in Firebase console → Authentication.
 *   2. Copy your web app config into `.env` (see keys below).
 *   3. (Optional) set VITE_USE_EMULATOR=true to use the local Auth emulator.
 */
import { initializeApp, getApps, getApp, type FirebaseOptions } from 'firebase/app';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  connectAuthEmulator,
  type Auth,
} from 'firebase/auth';

const firebaseConfig: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_MEASUREMENT_ID,
};

/** True when the minimum config needed to talk to Firebase Auth is present. */
export const isFirebaseConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

export const firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const auth: Auth = getAuth(firebaseApp);

// Keep the user signed in across page reloads / tabs.
void setPersistence(auth, browserLocalPersistence);

// Wire up the local Auth emulator when requested.
if (import.meta.env.VITE_USE_EMULATOR === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
}
